import Seo from "@/components/feature/Seo";
import PageHeader from "@/components/feature/PageHeader";
import SectionHeading from "@/components/feature/SectionHeading";
import { characters, relationshipEdges } from "@/mocks/siteData";
import { images } from "@/mocks/images";

function CharacterDetail({ id, flip }: { id: string; flip: boolean }) {
  const c = characters.find((item) => item.id === id)!;
  return (
    <article className="grid grid-cols-1 gap-6 rounded-lg border border-background-200/70 bg-background-100 p-4 md:grid-cols-2 md:p-6">
      <div className={`overflow-hidden rounded-lg bg-background-200 ${flip ? "md:order-2" : ""}`}>
        <img
          src={images[c.imgSeq]}
          alt={`${c.name}——${c.role}`}
          title={`洞洞杂货铺 ${c.name} ${c.role}`}
          className="aspect-[4/3] w-full object-cover object-top"
        />
      </div>
      <div className="flex flex-col">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-heading text-2xl font-black text-foreground-950">{c.name}</h2>
          <span className="rounded-full bg-primary-100 px-3 py-1 text-xs font-bold text-primary-800">
            {c.role}
          </span>
        </div>
        <p className="mt-1 text-sm text-foreground-600">{c.identity}</p>
        <p className="mt-4 text-sm leading-relaxed text-foreground-700">{c.intro}</p>
        <p className="mt-3 text-sm leading-relaxed text-foreground-700">{c.detail}</p>
        <div className="mt-auto flex flex-wrap gap-1.5 pt-4">
          {c.keywords.map((kw) => (
            <span
              key={kw}
              className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-900"
            >
              {kw}
            </span>
          ))}
        </div>
      </div>
    </article>
  );
}

const edgeIcon: Record<string, string> = {
  "祖孙传承": "ri-focus-2-line",
  "共同守护": "ri-shield-star-line",
  "村民羁绊": "ri-heart-3-line",
  "亦师亦友": "ri-team-line",
  "旧识之谜": "ri-question-line",
  "村民情谊": "ri-hand-heart-line",
};

export default function Characters() {
  const itemList = characters.map((c) => ({
    "@type": "ListItem",
    position: characters.indexOf(c) + 1,
    name: `${c.name}（${c.role}）`,
  }));

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "/" },
          { "@type": "ListItem", position: 2, name: "角色介绍大全", item: "/characters" },
        ],
      },
      {
        "@type": "CollectionPage",
        name: "洞洞杂货铺角色介绍大全",
        description: "洞洞杂货铺漫画主要人物介绍：店主、爷爷、小慧、欣茹与隔壁的神秘女性。",
        mainEntity: {
          "@type": "ItemList",
          itemListElement: itemList,
        },
      },
    ],
  };

  return (
    <>
      <Seo
        title="洞洞杂货铺角色介绍大全｜主要人物与人物关系详解"
        description="洞洞杂货铺漫画角色介绍大全：店主子强、前任店主爷爷、小慧、欣茹与隔壁神秘女性的身份、性格与人物关系详解，帮你看懂漫画里的每个人。"
        keywords="洞洞杂货铺角色介绍,洞洞杂货铺主要人物,洞洞杂货铺店主,人物关系"
        path="/characters"
        ogType="article"
        jsonLd={jsonLd}
      />
      <PageHeader
        crumb={[{ label: "首页", to: "/" }, { label: "角色介绍大全" }]}
        title="洞洞杂货铺角色介绍大全"
        subtitle="从继承店铺的新任店主子强，到村中的熟面孔，再到来历不明的隔壁邻居——漫画里的主要人物，一页铺开讲清楚。"
      />

      <section className="w-full bg-background-50 py-12 md:py-16">
        <div className="mx-auto w-full max-w-6xl space-y-6 px-4 md:px-6">
          {characters.map((c, idx) => (
            <CharacterDetail key={c.id} id={c.id} flip={idx % 2 === 1} />
          ))}
        </div>
      </section>

      <section id="relation" className="w-full bg-background-100 py-12 md:py-16">
        <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
          <SectionHeading
            eyebrow="人物关系"
            title="主要人物关系图"
            desc="店主的身份让这些人物彼此牵连，也把村庄的秘密织成一张网。"
          />
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {relationshipEdges.map((e) => (
              <div
                key={`${e.from}-${e.to}`}
                className="flex flex-col gap-3 rounded-lg border border-background-200/70 bg-background-50 p-5"
              >
                <div className="flex items-center justify-between gap-2 text-sm font-bold text-foreground-950">
                  <span>{e.from}</span>
                  <i className="ri-arrow-right-line text-foreground-400" />
                  <span>{e.to}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-foreground-600">
                  <i className={`${edgeIcon[e.type] ?? "ri-link"} text-accent-700`} />
                  <span className="rounded-md bg-accent-100 px-2 py-0.5 font-bold text-accent-800">
                    {e.type}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-xs text-foreground-500">
            提示：更完整的关系细节与角色成长，建议前往正版平台阅读《洞洞杂货店》原著。
          </p>
        </div>
      </section>
    </>
  );
}